import { useTranslation } from "react-i18next";
import { TextField } from "@/shared/ui/core";
import { RepeatableList, TextAreaField } from "@/features/admin/components";
import { EditorialBlock, type EditorialBlockLocaleProps } from "./EditorialBlock";
import { EditorialIconField } from "./EditorialIconField";
import { useBlockFilled } from "./useBlockFilled";

/**
 * Bloque 7 · El proceso: cómo se hace, paso por paso.
 *
 * Comparte esquema con los pilares —el contrato usa el mismo
 * `editorialPillarSectionSchema` para los dos— pero es su propio
 * componente y su propio bloque porque en la ficha se pinta numerado y
 * en orden, y ahí el ORDEN de las filas es información: por eso el
 * botón de subir y bajar de cada fila importa más aquí que en ningún
 * otro sitio.
 */
export function EditorialProcessBlock({ locale }: EditorialBlockLocaleProps) {
  const { t } = useTranslation();
  const name = `editorial.${locale}.process`;
  const filled = useBlockFilled(name);

  return (
    <EditorialBlock
      name={name}
      title={t("Proceso")}
      description={t("Los pasos, en orden. La ficha los numera sola.")}
      filled={filled}
    >
      <TextField
        name={`${name}.title`}
        label={t("Título del bloque")}
        placeholder={t("Cómo se hace")}
      />
      <TextAreaField name={`${name}.intro`} label={t("Entradilla")} rows={2} />

      <div className="sm:col-span-2">
        <RepeatableList
          name={`${name}.items`}
          max={12}
          addLabel={t("Añadir paso")}
          itemLabel={(index) => t("Paso {{n}}", { n: index + 1 })}
          emptyHint={t("Todavía no hay pasos. Añade el primero.")}
          newItem={() => ({ icon: "", title: "", description: "" })}
        >
          {({ name: row }) => (
            <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
              <TextField
                name={`${row}.title`}
                label={t("Nombre del paso")}
                placeholder={t("Extracción en frío")}
              />
              <EditorialIconField name={`${row}.icon`} />
              <TextAreaField
                name={`${row}.description`}
                label={t("Descripción")}
                rows={3}
                fieldClassName="sm:col-span-2"
              />
            </div>
          )}
        </RepeatableList>
      </div>
    </EditorialBlock>
  );
}
